import { configureStore } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'
import { bindActionCreators, Store } from 'redux'
import { createTransform, PersistConfig, persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage/session'

import middleware, { attach } from './combineMiddlewareLogic'
import { StateInterface } from './Interfaces/StateInterface'
import ongoing from './middleware/ongoing'
import reducer from './reducer'
import { processToken, TOKEN_NAME } from './reducers/token'

type StoreInterface = Store<StateInterface>

const config: PersistConfig<StateInterface> = {
  key: 'ZervantState',
  storage,
  blacklist: [
    'language',
    'config',
    'errors',
    'ongoing',
    'customers',
    'tracker',
    'subscriptionManagement',
    'navigation',
    'payNow',
    'reminders',
    'financing',
    'agerasAccounting',
    'referrals',
    'bankWithAccounts',
    'banking',
    'bankTransactions',
    'exchangeRate',
    'accounts',
  ],
  transforms: [
    createTransform(
      (state: StateInterface, key) => (sessionStorage.ZervantSession ? state : null),
      (state, key) => {
        /*
      If for some reason we have a token in sessionStorage, but not in state, lets get it there
      This needs to be called separately from dummyReducers since if there is a null token in stage
      initial stage actions wont take effect
      */
        if (key === 'token') {
          if (state === null && sessionStorage.getItem(TOKEN_NAME)) {
            try {
              return processToken(sessionStorage.getItem(TOKEN_NAME))
            } catch (e) {
              // There is no valid token, throw the user out
              sessionStorage.clear()
              window.location.assign('/login/login')
            }
          }
        }
        return state
      },
    ),
  ],
}
const persistedReducer = persistReducer(config, reducer)

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    })
      .prepend(middleware)
      .concat(ongoing),
})

export type AppDispatch = typeof store.dispatch
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>()

export function initializeStore(): Promise<StoreInterface> {
  return new Promise((resolve) =>
    persistStore(store, undefined, () => {
      attach(store)
      resolve(store)
    }),
  )
}
